import React from "react";
import { Dropdown } from "antd";
import { Link } from "react-router-dom";
import AuthService from "../../base/services/authentication.service";
import { isBusinessAccount } from "../../base/services/accountLevel.service";
import { UserRolesEnum } from "../../pages/auth/userRoles.enum";
import { useBillingStore } from "../../components/Billing/billingStore";
import {
   ProfileInformationIcon,
   LoginInformationIcon,
   TeamIcon,
   SubscriptionPlanIcon,
   UsageIcon,
   FundsIcon,
   TicketsIcon
} from "../../../_metronic/layout/components/aside/aside-menu/components/SidebarIcons";
import "./ProfileDropdown.scss";

// Role arrays mirror the ProtectedRoute guards in src/app/Routes.js — a role must
// not see a link it cannot open without landing on /unauthorized. `organizationOnly`
// does the same for a business account, which manages no team of its own (see
// base/services/accountLevel.service.js).
const MENU_GROUPS = [
   {
      key: "account-settings",
      label: "Account settings",
      items: [
         {
            key: "profile",
            title: "Profile",
            url: "/profile",
            icon: <ProfileInformationIcon />,
            role: [
               UserRolesEnum.SuperAdmin,
               UserRolesEnum.Organization,
               UserRolesEnum.AdminOfOrganization,
               UserRolesEnum.SeniorProjectManager,
               UserRolesEnum.JuniorProjectManager,
               UserRolesEnum.ProjectManager
            ]
         },
         {
            key: "login-security",
            title: "Login & Security",
            url: "/login-information",
            icon: <LoginInformationIcon />,
            role: [
               UserRolesEnum.SuperAdmin,
               UserRolesEnum.Organization,
               UserRolesEnum.AdminOfOrganization,
               UserRolesEnum.SeniorProjectManager,
               UserRolesEnum.JuniorProjectManager,
               UserRolesEnum.ProjectManager
            ]
         },
         {
            key: "team",
            title: "Team",
            url: "/admin-user-management",
            icon: <TeamIcon />,
            organizationOnly: true,
            role: [
               UserRolesEnum.SuperAdmin,
               UserRolesEnum.Organization,
               UserRolesEnum.AdminOfOrganization,
               UserRolesEnum.SeniorProjectManager
            ]
         }
      ]
   },
   {
      key: "billing",
      label: "Billing",
      items: [
         {
            key: "subscription",
            title: "Subscription",
            url: "/subscription-plans",
            icon: <SubscriptionPlanIcon />,
            // Shown by canManageBilling, not by role.
            billing: true
         },
         {
            key: "usage",
            title: "Usage",
            url: "/usage",
            icon: <UsageIcon />,
            role: [UserRolesEnum.Organization]
         },
         {
            key: "balance",
            title: "Balance",
            url: "/balance",
            icon: <FundsIcon />,
            role: [UserRolesEnum.Organization]
         }
      ]
   },
   {
      key: "help-center",
      label: "Help center",
      items: [
         {
            key: "tickets",
            title: "Tickets",
            url: "/tickets",
            icon: <TicketsIcon />,
            role: [
               UserRolesEnum.SuperAdmin,
               UserRolesEnum.Organization,
               UserRolesEnum.AdminOfOrganization,
               UserRolesEnum.SeniorProjectManager,
               UserRolesEnum.JuniorProjectManager,
               UserRolesEnum.ProjectManager,
               UserRolesEnum.Guest
            ]
         }
      ]
   }
];

const ProfileDropdown = ({ children }) => {
   const isBusiness = isBusinessAccount();
   const canManageBilling = useBillingStore((state) => state.canManageBilling);
   const isVisible = (item) =>
      (item.billing ? canManageBilling : AuthService.isAuthorized(item.role)) &&
      !(isBusiness && item.organizationOnly);

   const groups = MENU_GROUPS.map((group) => ({
      type: "group",
      key: group.key,
      label: group.label,
      children: group.items.filter(isVisible).map((item) => ({
         key: item.key,
         label: (
            <Link to={item.url} className="profile-dropdown__item">
               <span className="profile-dropdown__item-icon">{item.icon}</span>
               {item.title}
            </Link>
         )
      }))
   })).filter((group) => group.children.length > 0);

   const menuItems = [
      ...groups,
      ...(groups.length > 0 ? [{ type: "divider", key: "logout-divider" }] : []),
      {
         key: "logout",
         label: (
            <span className="profile-dropdown__item profile-dropdown__item--danger">Logout</span>
         ),
         onClick: AuthService.logout
      }
   ];

   return (
      <Dropdown
         menu={{ items: menuItems }}
         trigger={["click"]}
         placement="bottomRight"
         overlayClassName="profile-dropdown"
      >
         {children}
      </Dropdown>
   );
};

export default ProfileDropdown;
